/**
 * Capabilities (§5.3).
 *
 * A capability *is* a `MessagePort`: unforgeable, transferable, revocable by
 * closing the port. An app is mounted with exactly one — a `collection` scoped
 * to its own private store — and must obtain everything else through the
 * powerbox (§5.4).
 */

export type CapId = string;

export const CAP_KINDS = ["object", "collection", "stream", "exec"] as const;
export type CapKind = (typeof CAP_KINDS)[number];
export const CAP_KIND_SET: ReadonlySet<string> = new Set<string>(CAP_KINDS);

/**
 * The method vocabulary each kind of port answers to. Payload shapes land with
 * the broker implementation; the names are pinned here so both sides of the
 * wire agree on what a kind means.
 */
export const CAP_METHODS = {
  object: ["get", "set", "subscribe", "attrs"],
  collection: ["list", "create", "open", "subscribe"],
  // `resize` is PTY-shaped, and deliberately so: a terminal that cannot be
  // resized is not a terminal. A stream that has no geometry answers it with an
  // error, which is a capability declining a method rather than a new kind.
  stream: ["write", "onData", "close", "resize"],
  exec: ["spawn"],
} as const;

/**
 * An `exec` capability is scoped to one named Sprite, never to "the workspace"
 * (§10.3, resolved). A workspace has a home Sprite and any number of task
 * Sprites, so the grant has to say which — `CapRef.target` carries the name.
 * "Run this in my environment" and "run this in that agent's sandbox" must not
 * be the same authority.
 */

/** Narrows what the powerbox picker offers. Object types are opaque strings (§10.4). */
export type CapFilter = {
  objectTypes?: readonly string[];
  /** Shown to the user in the picker: what the app wants this for. */
  purpose?: string;
};

/** A capability as handed to an app: the live port plus enough to label it. */
export type GrantedCap = {
  id: CapId;
  kind: CapKind;
  port: MessagePort;
  objectType?: string;
  label?: string;
};

/**
 * A capability as recorded in the workspace document so it can be re-derived on
 * remount without re-prompting (§6.1 `Window.caps`).
 *
 * OPEN QUESTION (§10.2): the prototype trusts the workspace doc. Once auth
 * exists this re-grant has to be authenticated — a `CapRef` must not be a
 * bearer token that anyone with the doc can redeem.
 */
export type CapRef = {
  id: CapId;
  kind: CapKind;
  /** What the capability points at, interpreted by the broker for that kind. */
  target: string;
  objectType?: string;
  label?: string;
};
